import { NotificationCard } from '../../components/Card';
import { Button, Center, Flex, Icon, Text } from '@chakra-ui/react';
import { useMarkAsReadMutation } from '@/store/query/notification';
import { NotificationLink } from '../../components/NotificationLink';
import { NotificationTime } from '../../components/Time';
import { CheckCircle } from '@kula-ai/icons-core';
import { useCsvDownloadNotificationData } from './utils/useCsvDownloadNotificationData';

const CsvDownload = ({ notification = {}, onClose = () => {} }) => {
  const [markAsRead] = useMarkAsReadMutation();
  const { formTemplateName, recordsCount, csvExportUrl, surveyUrl } =
    useCsvDownloadNotificationData(notification);

  const handleDownload = () => {
    if (csvExportUrl) {
      markAsRead({ id: notification?.id });

      // Extract export ID from URL and navigate to local route
      try {
        const url = new URL(csvExportUrl);
        const exportId = url.pathname.split('/').pop();
        window.location.href = `/csv-exports/${exportId}`;
      } catch (error) {
        console.error('Error parsing CSV export URL:', error);
        // Fallback to direct navigation if URL parsing fails
        window.open(csvExportUrl, '_self');
      }
    }
  };

  return (
    <Flex alignItems="baseline" gap="6px">
      <Center
        minW="34px"
        h="34px"
        borderRadius="sm"
        bg="pastel-2-light"
        color="pastel-2-dark"
      >
        <Icon as={CheckCircle} width="16px" />
      </Center>
      <Flex flexDir="column" ml="16px" gap="8px" alignSelf="start" flex="1">
        <Text fontSize="sm">Survey data export is ready to be downloaded</Text>

        {formTemplateName && (
          <Flex alignItems="baseline">
            <Text fontSize="sm" fontWeight="bold" color="secondary-900">
              Survey:
            </Text>
            {surveyUrl ? (
              <Text fontSize="sm" fontWeight="bold" color="primary-600">
                <NotificationLink
                  to={surveyUrl}
                  label={formTemplateName}
                  onClose={onClose}
                  notification={notification}
                />
              </Text>
            ) : (
              <Text fontSize="sm" ml="4px">
                {formTemplateName}
              </Text>
            )}
          </Flex>
        )}

        {recordsCount !== undefined && (
          <Flex alignItems="baseline">
            <Text fontSize="sm" fontWeight="bold" color="secondary-900">
              Submissions:
            </Text>
            <Text fontSize="sm" ml="4px">
              {recordsCount}
            </Text>
          </Flex>
        )}

        <NotificationTime notification={notification} />

        {csvExportUrl && (
          <Button
            w="fit-content"
            variant="primary"
            borderRadius="8px"
            onClick={handleDownload}
            maxH="34px"
            data-test-id="download-csv-button"
          >
            Download CSV
          </Button>
        )}

        <Text fontSize="sm" color="gray.600">
          For security purposes this link will expire in 24 hours
        </Text>
      </Flex>
    </Flex>
  );
};

export const CsvDownloadNotification = ({
  notification = {},
  onClose = () => {},
}) => {
  return (
    <NotificationCard notification={notification}>
      <CsvDownload notification={notification} onClose={onClose} />
    </NotificationCard>
  );
};
